import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Alert,
} from 'react-native';
import { CameraView, useCameraPermissions, type BarcodeScanningResult } from 'expo-camera';
import { Ionicons } from '@expo/vector-icons';
import { extractPropuestaIdFromVotacionQr } from '../../lib/utils/votacionQrPayload';
import { extractRestauranteCodigoFromQr } from '../../lib/utils/restauranteQrPayload';

type ComensalQrVotacionScannerProps = {
  visible: boolean;
  onClose: () => void;
  onPropuestaId: (propuestaId: string) => void;
  /** Si se escanea un QR de restaurante (`/r/:uuid`), navegar u otra acción. */
  onRestauranteCodigoQr?: (codigoQr: string) => void;
};

export function ComensalQrVotacionScanner({
  visible,
  onClose,
  onPropuestaId,
  onRestauranteCodigoQr,
}: ComensalQrVotacionScannerProps) {
  const [permission, requestPermission] = useCameraPermissions();
  const [camReady, setCamReady] = useState(false);
  const [webCamAvailable, setWebCamAvailable] = useState<boolean | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      handledRef.current = false;
      setCamReady(false);
      setWebCamAvailable(null);
      return;
    }
    handledRef.current = false;
    if (Platform.OS === 'web') {
      CameraView.isAvailableAsync()
        .then(setWebCamAvailable)
        .catch(() => setWebCamAvailable(false));
    }
  }, [visible]);

  const applyScan = useCallback(
    (data: string) => {
      if (handledRef.current) return;
      const propId = extractPropuestaIdFromVotacionQr(data);
      if (propId) {
        handledRef.current = true;
        onPropuestaId(propId);
        onClose();
        return;
      }
      const codigoRest = extractRestauranteCodigoFromQr(data);
      if (codigoRest && onRestauranteCodigoQr) {
        handledRef.current = true;
        onRestauranteCodigoQr(codigoRest);
        onClose();
        return;
      }
      Alert.alert(
        'Código no reconocido',
        'Este QR no es un enlace de votación ni un código de restaurante de As Gastronómico.'
      );
    },
    [onClose, onPropuestaId, onRestauranteCodigoQr]
  );

  const onBarcodeScanned = useCallback(
    (result: BarcodeScanningResult) => {
      const data = result.data?.trim();
      if (data) applyScan(data);
    },
    [applyScan]
  );

  if (!visible) return null;

  const denied = permission?.status === 'denied';

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View style={styles.shell}>
        <View style={styles.topBar}>
          <Text style={styles.title}>Escanear QR</Text>
          <TouchableOpacity onPress={onClose} style={styles.closeBtn} accessibilityLabel="Cerrar escáner">
            <Ionicons name="close" size={28} color="#ffffff" />
          </TouchableOpacity>
        </View>

        {Platform.OS === 'web' && webCamAvailable === false ? (
          <View style={styles.messageBox}>
            <Ionicons name="camera-off-outline" size={40} color="#6b7280" />
            <Text style={styles.messageTitle}>Cámara no disponible</Text>
            <Text style={styles.messageBody}>
              En este dispositivo o navegador no hay cámara usable. Prueba desde el celular o permite el acceso a la
              cámara.
            </Text>
          </View>
        ) : permission == null ? (
          <View style={styles.centered}>
            <ActivityIndicator size="large" color="#ffffff" />
            <Text style={styles.hint}>Comprobando permisos…</Text>
          </View>
        ) : !permission.granted ? (
          <View style={styles.messageBox}>
            <Ionicons name="camera-outline" size={40} color="#6b7280" />
            <Text style={styles.messageTitle}>Permiso de cámara</Text>
            <Text style={styles.messageBody}>
              Necesitamos acceso a la cámara para leer el código QR (votación o restaurante).
            </Text>
            <TouchableOpacity style={styles.primaryBtn} onPress={() => requestPermission()} activeOpacity={0.88}>
              <Text style={styles.primaryBtnText}>Permitir cámara</Text>
            </TouchableOpacity>
            {denied ? (
              <Text style={styles.warn}>Si rechazaste el permiso, actívalo en la configuración del dispositivo.</Text>
            ) : null}
          </View>
        ) : (
          <View style={styles.cameraWrap}>
            <CameraView
              style={StyleSheet.absoluteFill}
              facing="back"
              barcodeScannerEnabled
              barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
              onCameraReady={() => setCamReady(true)}
              onMountError={() => {
                Alert.alert('Cámara', 'No se pudo iniciar la cámara.');
              }}
              onBarcodeScanned={onBarcodeScanned}
            />
            {!camReady ? (
              <View style={styles.previewOverlay}>
                <ActivityIndicator size="large" color="#ffffff" />
              </View>
            ) : null}
            <View style={styles.frameGuide} pointerEvents="none">
              <View style={styles.frameCorner} />
            </View>
            <View style={styles.bottomHint}>
              <Text style={styles.hint}>Enfoca el QR de votación o del restaurante</Text>
            </View>
          </View>
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: '#030213',
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: Platform.select({ ios: 56, android: 48, default: 24 }),
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
  },
  closeBtn: {
    padding: 8,
  },
  cameraWrap: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  previewOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  frameGuide: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
  },
  frameCorner: {
    width: 220,
    height: 220,
    borderRadius: 16,
    borderWidth: 3,
    borderColor: 'rgba(255,255,255,0.85)',
  },
  bottomHint: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: Platform.select({ ios: 40, android: 28, default: 24 }),
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  hint: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 15,
    textAlign: 'center',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  messageBox: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 28,
    gap: 12,
  },
  messageTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
    textAlign: 'center',
  },
  messageBody: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.75)',
    textAlign: 'center',
    lineHeight: 22,
  },
  primaryBtn: {
    marginTop: 12,
    backgroundColor: '#ffffff',
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 12,
  },
  primaryBtnText: {
    color: '#030213',
    fontSize: 16,
    fontWeight: '600',
  },
  warn: {
    marginTop: 16,
    fontSize: 13,
    color: '#fca5a5',
    textAlign: 'center',
  },
});
